<template>
  <img v-if="source" :src="source" @error="onError" />
  <img v-else-if="fallback" :src="fallback" />
</template>

<script>
import MediaService from "@/services/MediaService";

const mediaService = new MediaService();

/**
 * An <img> whose URL expires.
 *
 * Media URLs are minted when the payload carrying them is built and live for
 * config('media.signed_url_ttl') minutes, so a screen left open longer - a contract
 * form during a phone call, a vehicle list on a wall display - is holding sources
 * the object store no longer honours.
 *
 * On the first error this asks the server for a fresh URL for the record and tries
 * again. A second error is a real failure, a deleted object or a permission that
 * was revoked, and falls through to the placeholder rather than looping.
 *
 * The fresh URL is emitted as `refreshed` so a parent that renders the same URL
 * somewhere else - a download link around this image, say - can follow along.
 */
export default {
  name: "expiring-image",
  props: {
    src: { type: String, default: null },
    /**
     * Which record to ask for a fresh URL. Named recordId rather than id, because
     * a prop called id swallows the attribute of the same name: a caller writing
     * id="preview" to reach the element from CSS would silently be asking for the
     * URL of image "preview" instead.
     */
    type: { type: String, default: "image" },
    recordId: { type: [Number, String], default: null },
    /** Which key of the response to take; a thumbnail wants thumb_url. */
    urlKey: { type: String, default: "url" },
    fallback: { type: String, default: null }
  },
  data() {
    return {
      fresh: null,
      exhausted: false,
      attempts: 0
    };
  },
  computed: {
    source() {
      if (this.exhausted) return null;

      return this.fresh || this.src || null;
    }
  },
  watch: {
    /**
     * A new src is a new image, so the retry is armed again - unless it is the URL
     * this component just handed to its parent. A parent that stores `refreshed`
     * back onto the record it renders (the attachments tab does, to keep the
     * download link in step) would otherwise re-arm the retry on every refresh and
     * loop: error, refresh, src changes, retry armed, error. Observed in the
     * browser against an image the decoder rejects, where the error repeats
     * whatever the URL.
     */
    src(value) {
      if (value === this.fresh) return;

      this.fresh = null;
      this.exhausted = false;
      this.attempts = 0;
    }
  },
  methods: {
    onError() {
      if (this.attempts > 0 || !this.recordId) {
        this.exhausted = true;

        return;
      }

      this.attempts++;

      mediaService
        .freshUrls(this.type, this.recordId)
        .then(urls => {
          const url = urls && urls[this.urlKey];

          if (!url) {
            this.exhausted = true;

            return;
          }

          this.fresh = url;
          this.$emit("refreshed", url);
        })
        .catch(() => {
          this.exhausted = true;
        });
    }
  }
};
</script>
